import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ProductCartItem } from '../../../types/common';
import './overQuantityModal.scss';

interface OverQuantityModalProps {
    products: ProductCartItem[];
    onClose: () => void;
    onConfirm: () => void;
}

const OverQuantityModal: React.FC<OverQuantityModalProps> = ({ products, onClose, onConfirm }) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const safeProducts = useMemo(() => (Array.isArray(products) ? products : []), [products]);

    const getFocusable = useCallback((): HTMLElement[] => {
        const root = panelRef.current;
        if (!root) return [];
        const nodes = root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        return Array.from(nodes).filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    }, []);

    useEffect(() => {
        previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusables = getFocusable();
        (focusables[0] ?? panelRef.current)?.focus();
        return () => {
            document.body.style.overflow = prevOverflow;
            previouslyFocusedRef.current?.focus?.();
        };
    }, [getFocusable]);

    const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            return;
        }
        if (e.key !== 'Tab') return;
        const focusables = getFocusable();
        if (focusables.length === 0) {
            e.preventDefault();
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
            if (!active || active === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (!active || active === last) {
                e.preventDefault();
                first.focus();
            }
        }
    };

    return (
        <div
            className="over-quantity-modal__backdrop"
            onMouseDown={handleBackdropMouseDown}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-labelledby="over-quantity-modal-title"
        >
            <div className="over-quantity-modal__panel" ref={panelRef} tabIndex={-1}>
                <h2 className="over-quantity-modal__title" id="over-quantity-modal-title">
                    Внимание!
                </h2>

                <div className="over-quantity-modal__content">
                    {safeProducts.map((item) => {
                        const { name, quantity, availableQuantity } = item;
                        const shortage = Math.max((quantity ?? 0) - (availableQuantity ?? 0), 0);

                        return (
                            <p className="over-quantity-modal__item" key={item.productId}>
                                <strong className="over-quantity-modal__item-title">{name}</strong> ({availableQuantity} шт. в наличии / в корзине {quantity} шт.)
                                <br />
                                Быстрая доставка доступна для {availableQuantity} шт. Для {shortage} шт. доставка будет осуществлена в срок от 20 дней.
                            </p>
                        );
                    })}
                </div>

                <div className="over-quantity-modal__buttons">
                    <button type="button" className="over-quantity-modal__button over-quantity-modal__button--cancel" onClick={onClose}>
                        Отмена
                    </button>
                    <button type="button" className="over-quantity-modal__button over-quantity-modal__button--confirm" onClick={onConfirm}>
                        Продолжить оформление
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OverQuantityModal;
